import { 
  Box, Paper, Typography, TextField, Button, Alert, 
  Container, CircularProgress, Divider
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import SimpleCaptcha from '../components/auth/SimpleCaptcha';
import AuthService from '../services/AuthService';
import { log } from '../utils/errors/logger';

const Register = () => {
  const { t } = useTranslation();
  
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [company, setCompany] = useState('');
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaValid, setCaptchaValid] = useState(false);
  
  const validateForm = () => {
    // Reset errors
    setError('');
    
    // Check required fields
    if (!username || !email || !password || !confirmPassword) {
      setError(t('register.validation.requiredFields'));
      return false;
    }
    
    // Check captcha
    if (!captchaValid) {
      setError(t('register.validation.captcha', 'Bitte lösen Sie die Sicherheitsabfrage'));
      return false;
    };
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(t('register.validation.invalidEmail'));
      return false;
    }
    
    // Check password match
    if (password !== confirmPassword) {
      setError(t('register.validation.passwordMismatch'));
      return false;
    }
    
    // Check password strength
    if (password.length < 8) {
      setError(t('register.validation.passwordTooShort'));
      return false;
    }
    
    return true;
  };
  
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      await AuthService.register(
        username, 
        email, 
        password, 
        fullName, 
        company
      );
      
      setSuccess(t('register.success'));
      setError('');
      
      // Clear form
      setUsername('');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setFullName('');
      setCompany('');
      
    } catch (error: any) {
      log.error('Registration error', { error: error.message, username, email });
      setError(error.message || t('register.error'));
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Logo container */}
      <Box
        sx={{
          position: 'relative',
          display: { xs: 'none', md: 'flex' },
          height: '100vh',
          width: '40%',
          alignItems: 'center',
          justifyContent: 'flex-end',
          mr: -112,
          zIndex: 0,
        }}
      >
        <Box
          component="img"
          src="/cpro-companion-single-transparent.png"
          alt="CPRO Companion"
          sx={{
            maxWidth: '100%',
            maxHeight: '80vh',
            objectFit: 'contain',
            opacity: 0.5,
          }}
        />
      </Box>

      {/* Registration form container */}
      <Container component="main" maxWidth="sm" sx={{ zIndex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            position: 'relative',
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Typography component="h1" variant="h4" color="primary">
                {t('register.title', 'Registrieren')}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {t('register.subtitle', 'Erstellen Sie ein neues Konto')}
              </Typography>
            </Box>
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}
            
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label={t('register.username', 'Benutzername')}
                name="username"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={loading}
              />
              
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label={t('register.email', 'E-Mail-Adresse')}
                name="email"
                autoComplete="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
              
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label={t('register.password', 'Passwort')}
                type="password"
                id="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                helperText={t('register.passwordHint', 'Mindestens 8 Zeichen')}
              />
              
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label={t('register.confirmPassword', 'Passwort bestätigen')}
                type="password"
                id="confirmPassword"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
              />
              
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {t('register.optionalInfo', 'Optionale Informationen')}
              </Typography>
              
              <TextField
                margin="normal"
                fullWidth
                id="fullName"
                label={t('register.fullName', 'Vollständiger Name')}
                name="fullName"
                autoComplete="name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                disabled={loading}
              />
              
              <TextField
                margin="normal"
                fullWidth
                id="company"
                label={t('register.company', 'Unternehmen')}
                name="company"
                autoComplete="organization"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                disabled={loading}
              />
              
              {/* Captcha-Komponente */}
              <SimpleCaptcha 
                onChange={setCaptchaValid}
                isValid={captchaValid}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
                disabled={loading || !captchaValid}
              >
                {loading ? <CircularProgress size={24} /> : t('register.signUp', 'Registrieren')}
              </Button>
            </Box>
            
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2">
                {t('register.alreadyHaveAccount', 'Haben Sie bereits ein Konto?')}{' '}
                <Link to="/login" style={{ textDecoration: 'none' }}>
                  {t('register.signIn', 'Anmelden')}
                </Link>
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default Register;
